import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { IViewStructureNode } from '../../../redux/project/sliceProjectViewData';
import { IAppReduxStore } from '../../../redux/store';
import DynamicTree from '../../General/DynamicTree/DynamicTree';
import {
    DynamicTreeItemCategory,
    ITreeItemCategoryProps,
} from '../../General/DynamicTree/DynamicTreeItemCategory';
import { ITreeNode, sortData } from '../../General/DynamicTree/DynamicTreeUtils';
import { AutoSizer, List } from 'react-virtualized';
import 'react-virtualized/styles.css';

const defaultRowHeight = 40;

type WithChildren<P = unknown> = P & { children?: ReactElement<WithChildren<P>>[] };

const notEmpty = <T extends object>(obj: T): boolean => !!Object.keys(obj).length;

const deepFilter = (obj: ITreeNode<IViewStructureNode>, filter): ITreeNode<IViewStructureNode> => {
    const newObj = {} as ITreeNode<IViewStructureNode>;
    const currentCats = Object.entries(obj.categories);
    currentCats.forEach(([name, value]) => {
        if (notEmpty(value.categories)) {
            const filteredCategories = deepFilter(value, filter);
            if (notEmpty(filteredCategories)) {
                newObj[name] = {
                    ...(value as object),
                    categories: filteredCategories,
                };
            }
        } else if (filter(value)) {
            newObj[name] = value;
        }
    });

    return newObj;
};

const getTreeUniqueKeys = (obj: ITreeNode<IViewStructureNode>): string[] => {
    const ids = [];

    if (notEmpty(obj.categories)) {
        Object.values(obj.categories).forEach(cat => {
            getTreeUniqueKeys(cat).forEach(item => ids.push(item));
        });
    }
    ids.push(obj.path);

    return ids.filter(Boolean);
};

/**
 * TreeView that shows the structure of a project database view.
 */
export default ({
    searchQuery,
    expanded,
    handleNodeToggle,
    isExpandedAll,
}: IProjectViewTreeProps) => {
    /** Allow component to change the URL. */
    const navigate = useNavigate();
    const listRef = useRef<List>(null);

    /** Get the replicaId, viewName and category from the react router. */
    const { replicaId, areaName, viewName, category } = useParams();

    const [disableTransition, setDisableTransition] = useState(false);

    /** Access relevant state variables from the redux store. */
    const structure = useSelector((store: IAppReduxStore) => store.projectViewData.structure.data);
    const viewTree = useSelector(
        (store: IAppReduxStore) => store.projectViewData.structure.viewTree,
    );

    /** Determine if one of the view categories has the same id as the category id from the URL. */
    const categoryMatch = structure?.filter(x => x.id === category)[0];

    /** Get an array of the selected categories in the TreeView. */
    const selected = (categoryMatch?.category ?? '')
        .split('\\')
        .map((_, i, arr) => arr.slice(0, i + 1).join('\\'));

    /**
     * Handles the onClick behaviour for a TreeView Node.
     * @param node - Target TreeView Node.
     */
    const selectTreeViewNode = (node: ITreeNode<IViewStructureNode>) => {
        navigate(`/project/${replicaId}/${areaName}/${viewName}/${node.data[0].id}`);
    };

    const filterCategories = s => {
        return s.path?.toLowerCase().includes(searchQuery.toLowerCase());
    };

    /**
     * Creates a new TreeItemCategory.
     * @param node - Target node object.
     */
    const categoryTreeItem = useCallback(
        (node: ITreeNode<IViewStructureNode>, paddingLeft: number) => {
            const itemProps: ITreeItemCategoryProps & {
                paddingLeft: number;
                disableTransition?: boolean;
                isExpandedAll?: boolean;
            } = {
                itemId: node.path,
                labelText: node.title,
                active: !!categoryMatch?.category?.startsWith(node.path),
                onClick: () => selectTreeViewNode(node),
                count: +node.data[0]?.docCount,
                compact: true,
                paddingLeft,
                disableTransition,
                isExpandedAll,
            };

            return (
                <DynamicTreeItemCategory key={node.path} {...itemProps}>
                    {sortData<IViewStructureNode, string>(node.categories, 'category', {
                        stringsAZ: true,
                        datesDesc: true,
                    }).map(x => categoryTreeItem(node.categories[x], paddingLeft + 8))}
                </DynamicTreeItemCategory>
            );
        },
        [categoryMatch, selectTreeViewNode, disableTransition],
    );

    const filteredTreeView = useMemo(() => {
        return searchQuery
            ? ({
                  categories: deepFilter(viewTree, filterCategories),
              } as unknown as ITreeNode<IViewStructureNode>)
            : viewTree;
    }, [searchQuery, viewTree]);

    useEffect(() => {
        if (searchQuery) {
            handleNodeToggle(getTreeUniqueKeys(filteredTreeView));
        }
    }, [searchQuery, filteredTreeView]);

    /** Create tree nodes from sorted data. */
    const nodes = useMemo(() => {
        if (!filteredTreeView?.categories) {
            return [];
        }

        return sortData<IViewStructureNode, string>(filteredTreeView.categories, 'category', {
            stringsAZ: true,
            datesDesc: true,
        });
    }, [searchQuery, filteredTreeView]);

    useEffect(() => {
        if (!listRef?.current) return;
        setDisableTransition(false);
        if (expanded.length === 0) {
            setDisableTransition(true);
        }

        listRef.current.recomputeRowHeights();
        listRef.current.forceUpdate();
    }, [listRef, expanded]);

    const getExpandedItemCount = (
        item: ReactElement<WithChildren<ITreeItemCategoryProps>>,
        arr: string[],
    ) => {
        let count = 1;

        const { props } = item;
        if (arr.includes(props.itemId)) {
            count += props.children
                .map(itemChild => {
                    return getExpandedItemCount(itemChild, arr);
                })
                .reduce((total, count) => {
                    return total + count;
                }, 0);
        }

        return count;
    };

    const rowHeight = useCallback(
        (index: number) => {
            if (!filteredTreeView) return defaultRowHeight;
            const node = categoryTreeItem(filteredTreeView.categories[nodes[index]], 4);
            const arr = expanded.filter(item => item.startsWith(node.props.itemId));

            return getExpandedItemCount(node, arr) * defaultRowHeight;
        },
        [expanded, searchQuery],
    );

    const rowRenderer = ({ key, index, style }) => {
        return (
            <div key={key} style={{ ...style }}>
                {categoryTreeItem(filteredTreeView.categories[nodes[index]], 4)}
            </div>
        );
    };

    return !structure?.length ? (
        <></>
    ) : (
        <DynamicTree
            selectedItems={selected}
            expandedItems={expanded}
            onExpandedItemsChange={(_, itemIds) => {
                handleNodeToggle(itemIds);
            }}
            onItemExpansionToggle={(ev, item, isExpaned) => {
                setDisableTransition(!isExpaned);
            }}
        >
            <AutoSizer>
                {({ height, width }) => (
                    <List
                        height={height}
                        overscanRowCount={3}
                        ref={listRef}
                        rowCount={nodes.length}
                        rowHeight={({ index }) => rowHeight(index)}
                        rowRenderer={rowRenderer}
                        width={width}
                    />
                )}
            </AutoSizer>
        </DynamicTree>
    );
};

/**
 * Interface that describes the props for a ProjectViewTree component.
 */
export interface IProjectViewTreeProps {
    /** Item expanded tree items. */
    expanded: string[];

    /** Item handleNodeToggle handler. */
    handleNodeToggle: (itemIds: string[]) => void;

    searchQuery: string;

    isExpandedAll: boolean;
}
